import type React from "react";

import type { PollDto, UserDto } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { CheckDone02Icon } from "@/components/Icons/CheckDone02Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { pollMutations } from "@/queries/polls/mutations";
import { useTranslation } from "@/translations";

interface CommunityPollResultSectionProps {
  poll: PollDto;
}

export function CommunityPollResultSection({ poll }: CommunityPollResultSectionProps): React.ReactNode {
  const { t } = useTranslation();
  const [isClearVotesConfirmModalOpened, clearVotesConfirmModalHandler] = useBool(false);

  const sessionUser = useSessionUser();
  const deletePollVotesMutation = pollMutations.useDeletePollVotes();

  const amountTotalResults = (poll.results || []).reduce((acc, result) => acc + result.count, 0);
  const isPollEnded = poll.endsAt && new Date(poll.endsAt) < new Date();

  return (
    <>
      <div className="flex flex-col gap-4">
        {poll.allowMultipleAnswers && !isPollEnded && (
          <div className="flex w-full gap-1 text-grey-700">
            <CheckDone02Icon size={24} />
            <span className="text-caption">{t("component.community-poll.form.label")}</span>
          </div>
        )}
        {poll.canRespond === "alreadyAnswered" && (
          <Button
            styling="ghostPrimary"
            data-testid="community-poll-clear-own-votes-btn"
            onClick={clearVotesConfirmModalHandler.setTrue}
          >
            {t("component.community-poll.button.clear-votes")}
          </Button>
        )}

        <ul className="flex w-full flex-col gap-2" data-testid="community-poll-result-section">
          {(poll.results || []).map((result) => (
            <li
              className="relative w-full overflow-hidden rounded-lg border border-grey-300 p-4"
              key={result.answer.id}
            >
              <div className="relative z-20 flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  {result.voters.find((voter) => voter.id === sessionUser.id) && (
                    <div
                      className="flex size-4 items-center justify-center rounded-full bg-white"
                      data-testid="community-poll-own-vote-indicator"
                    >
                      <CheckIcon size={12} className="text-aop-dark-blue-500" />
                    </div>
                  )}
                  <span className="text-body">{result.answer.text}</span>
                  {result.hasWon && <Star01Icon className="fill-yellow-500 text-yellow-500" size={20} />}
                </div>

                <div className="flex items-center">
                  {result.voters.length > 0 && <VotersList voters={result.voters.slice(0, 3)} />}
                  <span className="flex w-6 justify-center text-body-bold">{result.count}</span>
                </div>
              </div>

              {/* Progress background */}
              <div
                className="absolute left-0 top-0 z-0 flex h-full items-center bg-grey-300/50 duration-500 ease-out animate-in slide-in-from-left-full"
                style={{
                  width: amountTotalResults === 0 ? 0 : `${(result.count / amountTotalResults) * 100}%`,
                }}
              />
            </li>
          ))}
        </ul>
      </div>

      <ConfirmModal
        data-testid="community-poll-clear-own-votes-confirm-modal"
        title={t("component.community-poll.clear-votes-modal.title")}
        description={t("component.community-poll.clear-votes-modal.description")}
        isOpened={isClearVotesConfirmModalOpened}
        onOpenChange={clearVotesConfirmModalHandler.set}
        shouldCloseOnEsc
        onReject={clearVotesConfirmModalHandler.setFalse}
        rejectBtnProps={{
          text: t("component.community-poll.clear-votes-modal.button.cancel"),
        }}
        onResolve={async () => {
          await deletePollVotesMutation.mutateAsync({ pollId: poll.id });
          clearVotesConfirmModalHandler.setFalse();
        }}
        resolveBtnProps={{
          text: t("component.community-poll.clear-votes-modal.button.confirm"),
          "data-testid": "confirm-btn",
        }}
        isLoading={deletePollVotesMutation.isPending}
      />
    </>
  );
}

type VotersListProps = {
  voters: UserDto[];
};

function VotersList({ voters }: VotersListProps): React.ReactNode {
  return (
    <ul className="flex items-center">
      {voters.map((voter) => (
        <li key={voter.id} className="z-10 size-6 rounded-full border-2 border-white [&:not(&:first-child)]:-ml-2">
          <UserAvatar img={voter.avatar} />
        </li>
      ))}
    </ul>
  );
}
